import { useCallback, useEffect, useRef, useState } from 'react';
import {
  StepCta,
  StepHeading,
  StepSub,
  TertiaryLink,
} from '../../OnboardingShell/StepLayout';
import { OptionRow } from '../../primitives';
import { Icon } from '../../icons/registry';
import {
  geocodeCityOnSave,
  reverseGeocode,
  roundToCityLevel,
} from '../../../lib/locationGeocode';
import type { OnboardingAnswers } from '../../../hooks/onboardingReducer';
import styles from './Step2Location.module.css';

/**
 * ⚖️ B58 — one plausible place the typed query could mean.
 *
 * The parts are kept SPLIT rather than as one display string because the
 * founder's complaint is precisely that the parts are missing: a city name on
 * its own is ambiguous across countries, and the Country box never filled in
 * because nothing ever handed the step a country to put there.
 *
 * `country` is empty far more often than it should be, and that is a SERVER
 * gap, not a component one — see the B58 block on `toCandidate` below.
 */
export type LocationCandidate = {
  /** Display label as the geocoder gave it ("Bucaramanga, Santander"). */
  label: string;
  city: string;
  region: string;
  country: string;
  /** §8.2 — already rounded to city level at capture. */
  lat: number;
  lon: number;
};

type Step2Props = {
  answers: OnboardingAnswers;
  setAnswer: (patch: Partial<OnboardingAnswers>) => void;
  onContinue: () => void;
  /**
   * ⚖️ B58 SEAM — how a typed query becomes candidates. Defaults to the
   * forward geocoder, which today can answer with AT MOST ONE match
   * (`/api/geocode` returns `{ match }`, and `api/lib/apple-data.js`
   * `geocodeCity` takes `results[0]` and drops the rest). The chooser below is
   * therefore built and tested but DORMANT against the current server: the day
   * the endpoint answers with a list, this default is the one line that
   * changes, and no UI work is needed.
   *
   * ⛔ Not a general extension point for callers. `routes/onboarding.tsx` does
   * not pass it and must not start to; it exists so the multi-candidate UI is
   * exercised by real tests instead of shipping unproven.
   */
  resolveCandidates?: (query: string, signal?: AbortSignal) => Promise<LocationCandidate[]>;
};

/** `geocodeCityOnSave` no-ops below 3 chars, measured on the COMPOSED query
 *  it actually sends. Gating on the city field alone was stricter than the
 *  wrapper and made short-but-valid cities uncheckable ("Ho" + "Vietnam",
 *  review finding F-4) — the UI now mirrors the wrapper exactly. */
const MIN_QUERY_CHARS = 3;

/** B442 — the other end. `/api/geocode` refuses a query over 120 characters
 *  (`handleGeocode`, pinned by a test against the server text), and the saved
 *  location IS that composed query, so the step refuses it at the same length
 *  instead of saving text that can never be looked up. */
const MAX_QUERY_CHARS = 120;
const LIMIT_TEXT = 'Use 120 characters or fewer for the city and country.';

type LookupState =
  | { kind: 'idle' }
  | { kind: 'checking' }
  /** ⚖️ B48 — `lat`/`lon` joined this state, and they are the whole fix on the
   *  checked path. `geocodeCityOnSave` has always answered `{label, lat, lon}`;
   *  this state kept the label alone, so `handleContinue` had nothing to save
   *  and the athlete's row got a city with null coordinates. They are stored
   *  ALREADY ROUNDED to city level (§8.2) — the precise centroid never enters
   *  component state. */
  | { kind: 'match'; label: string; lat: number; lon: number }
  /** ⚖️ B58 — MORE THAN ONE PLAUSIBLE PLACE, and the athlete picks. Reached
   *  only when the resolver hands back 2 or 3 candidates; exactly one is still
   *  the ratified `match` card, unchanged, so nothing about today's walk moves.
   *  Capped at 3 by `MAX_CANDIDATES` — a list long enough to need scrolling is
   *  a search result, and this step does not run a search. */
  | { kind: 'choose'; candidates: LocationCandidate[] }
  | { kind: 'no-match' }
  | { kind: 'failed' };

/** ⚖️ B58 — at most three options. See the `choose` state above. */
const MAX_CANDIDATES = 3;

/**
 * ⚖️ B58 — the device-position request the founder asked for, as its OWN state.
 *
 * ⛔ Deliberately NOT folded into `LookupState`. That machine carries the
 * CD 2026-08-02 §3 retry anatomy (focus rules, the mounted failed note, the
 * one-tap guard), and every one of those behaviours is pinned by test. A second
 * concern sharing those kinds would make each of those pins ambiguous about
 * which request it was describing.
 *
 * ⚖️ O2 (CD onboarding-chapters handoff, founder-ratified 2026-08-30) —
 * B58 made `declined` ONE state for every unhappy ending and wrote "⛔ Do not
 * split this into per-error copy". CD HAS NOW RULED THE OTHER WAY, and B58's
 * own copy list was flagged provisional pending exactly this ruling ("Ratified
 * surface, so CD owns the final wording"). The handoff enumerates two endings
 * with two postures, because they are two different facts about the world:
 *
 *   · `declined` — SHE SAID NO. Neutral note, no retry offered, and never
 *     re-prompted: "No problem. Type your city instead." Retrying a refusal is
 *     nagging, and the OS would not ask again anyway.
 *   · `failed` — THE LOOKUP DID NOT LAND (position unavailable, timed out, a
 *     shell that never answered). Neutral note with a Retry, because a retry
 *     genuinely can change this answer.
 *
 * ⛔ THIS IS THE WHOLE SPLIT. It is not the error taxonomy B58 refused — the
 * discrimination is on ONE bit (`code === PERMISSION_DENIED`), both endings
 * stay neutral and calm, neither blames the athlete, and the typed field is
 * still right there in both. ⛔ Do not grow a third: "position unavailable"
 * and "timed out" are the same fact to the person waiting.
 *
 * Note the same rule the shipped lookup already follows, stated in the
 * handoff: a statement about the athlete's INPUT is amber; a transport failure
 * is neutral. A refusal and a failed fix are both neutral.
 */
type PositionState =
  | { kind: 'idle' }
  | { kind: 'locating' }
  /** A fix AND a name for it. */
  | { kind: 'located'; label: string; lat: number; lon: number }
  /** A fix with no name — reverse geocoding had nothing to say. The coordinates
   *  are still the athlete's own and still worth saving; she names the place.
   *  ⚠️ CD did NOT rule on this state (the handoff's Find-my-location section
   *  lists locating / resolved / ambiguous / declined / transport failure and
   *  no fifth), so B58's provisional sentence stands here verbatim. 📋 Flagged
   *  in the O2 lane report as the one position string still awaiting CD. */
  | { kind: 'unresolved'; lat: number; lon: number }
  /** Permission refused. ⛔ No retry: see the split above. */
  | { kind: 'declined' }
  /** The lookup did not land. Retryable, and the note says so. */
  | { kind: 'failed' };

/** `GeolocationPositionError.PERMISSION_DENIED`. The ONE bit the split reads —
 *  named rather than inlined so the next reader does not have to look up what
 *  `1` meant. */
const PERMISSION_DENIED = 1;

/**
 * Split a geocoder label into the parts the chooser shows and the Country box
 * needs.
 *
 * ⚠️ B58 SERVER GAP, stated where the next reader will hit it. `country` is
 * almost always EMPTY today, and that is why the founder's Country box never
 * fills. Apple hands the server a `structuredAddress` that HAS a country, and
 * the server drops it on the floor in two places, both read-only to this lane:
 *
 *   · `api/lib/apple-data.js` `geocodeCity` builds its label from
 *     `[locality, administrativeArea]` only, with the comment "no country
 *     spam" — so a forward match is "City, Region" and never carries a country;
 *   · `api/lib/apple-data.js` `reverseGeocodeCity` returns `{ city, region }`
 *     and uses `country` only as a LAST-RESORT fallback for `region`.
 *
 * So this function reads a third label part when there is one, and reads a
 * `country` field off the match object when the server ever starts sending one
 * (additive, and inert until then). Neither is available today. ⛔ Do not
 * "fix" this by inferring a country from a region name in the client: a lookup
 * table of regions to countries is a guess dressed as data, and it would be
 * wrong first for exactly the international athletes F5 was filed for.
 */
function toCandidate(match: { label: string; lat: number; lon: number }): LocationCandidate {
  const parts = match.label
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const extra = (match as { country?: unknown }).country;
  return {
    label: match.label,
    city: parts[0] ?? match.label,
    region: parts[1] ?? '',
    country: typeof extra === 'string' && extra.trim() ? extra.trim() : (parts[2] ?? ''),
    // ⚖️ §8.2 — rounded at capture, the moment a coordinate enters the
    // component. Nothing downstream ever sees the geocoder's own precision.
    lat: roundToCityLevel(match.lat),
    lon: roundToCityLevel(match.lon),
  };
}

/** The default `resolveCandidates` — see the prop's docblock for why it can
 *  only ever return 0 or 1 today, and what changes when the server can do
 *  better. */
async function resolveCandidatesViaGeocoder(
  query: string,
  signal?: AbortSignal,
): Promise<LocationCandidate[]> {
  const match = await geocodeCityOnSave(query, signal);
  return match ? [toCandidate(match)] : [];
}

/** One line for a chooser option: the parts the athlete needs to tell two
 *  same-named places apart, and nothing else. */
function candidateLine(candidate: LocationCandidate): string {
  return [candidate.city, candidate.region, candidate.country].filter(Boolean).join(', ');
}

/** A candidate, narrowed to the settled-answer shape the `match` state and
 *  `handleContinue` have always spoken. */
function matchFromCandidate(candidate: LocationCandidate): {
  label: string;
  lat: number;
  lon: number;
} {
  return { label: candidate.label, lat: candidate.lat, lon: candidate.lon };
}

/**
 * ⚖️ B58 — FEATURE DETECTION, read once at mount.
 *
 * The button is offered only where a position API exists. On the Capacitor iOS
 * WKWebView `navigator.geolocation` IS present, so the button renders there and
 * the OS prompt is what actually decides — which is the design: the tap is the
 * consent, the system prompt seals it. If the shell answers oddly or never
 * answers, the 10 s timeout lands in `declined` and the athlete is back on the
 * typed field with one calm line. See the lane report: native behaviour needs a
 * device verify on the next build.
 */
function hasPositionSupport(): boolean {
  if (typeof navigator === 'undefined') return false;
  const geo = (navigator as Navigator & { geolocation?: Geolocation }).geolocation;
  return typeof geo?.getCurrentPosition === 'function';
}

/** Compose the free-text query the geocoder sees. City-level only — we never
 *  send coordinates from this step, and the query is never logged.
 *
 *  ⚖️ §8.3 AUDIT NOTE (2026-08-16, founder-flagged). This step WAS a Nominatim
 *  caller — through `geocodeCityOnSave` — so the OSM Foundation received every
 *  city an athlete typed here at onboarding, plus their IP. It never sent
 *  COORDINATES: this step has no `navigator.geolocation` call and never has
 *  (see F5(a) below), so the raw-fix exposure was Settings-only. The wrapper
 *  now routes to our own `/api/geocode`, which is the whole of the fix here —
 *  this file's own logic did not change. */
function composeQuery(city: string, country: string): string {
  return [city, country].filter(Boolean).join(', ');
}

/**
 * Step 2 — Location.
 *
 * ## F5(a) — no fabricated "detected" location
 *
 * This step used to open on a spinner ("Finding your location…") and then a
 * "Detected location" card reading `Los Altos, CA, United States` — a
 * hardcoded constant, not a detection. Nothing in this component has ever
 * called `navigator.geolocation` or an IP-geo backend, so the card was
 * theatre; a real user in Bucaramanga, Colombia was shown a California city
 * and asked to confirm it (field-test finding F5).
 *
 * The detect states are gone. There is one view: empty inputs with example
 * placeholders. If a real detection path is ever wired here, it must set the
 * value from an actual lookup — a constant must never masquerade as one.
 *
 * ## B48 (2026-08-29) — "auto-detect never works" was not a broken request
 *
 * The founder walked onboarding three times on native Build 25 and reported
 * the auto-detect as never working. B48 established what the code actually
 * did, because "never works" reads like a request that hangs and it was not
 * one: THE STEP MADE NO POSITION REQUEST AT ALL. There was no
 * `navigator.geolocation` call, no Capacitor plugin call, no IP-geo backend —
 * no detection to fail. F5(a) above had deleted the only thing that ever
 * looked like one, and it was a hardcoded string. B48 could not honestly wire
 * one in (no plugin budget, an `NSLocationWhenInUseUsageDescription` that
 * promises location is for mapping RUNS, and a minor walks this step), so it
 * did the next honest thing: it SAID SO, in one sentence, and pinned a drift
 * guard asserting no position API was referenced in this file.
 *
 * ## B58 (2026-08-29) — the founder's ruling: a button, and the tap is consent
 *
 * ⚖️ THIS SUPERSEDES B48's "no detection" posture, on founder direction. The
 * objection B48 recorded was never to reading a position; it was to reading one
 * WITHOUT BEING ASKED, at signup, from someone who might be twelve. A button
 * answers all of it:
 *
 *   · nothing is requested on mount, on typing, on Check, or on Continue —
 *     ONLY on the tap. The tap is the athlete's consent, and the OS permission
 *     prompt seals it. Both halves are pinned by test (behavioural and
 *     literal), and that pin REPLACES B48's "no position API in this source"
 *     guard, which the button makes false;
 *   · declining costs nothing. Denial, timeout, an unhelpful shell — all land
 *     in one `declined` state with one calm line, and the typed city, which was
 *     always the real path, is still right there;
 *   · a shell with no position API at all never sees the button, and keeps
 *     B48's sentence, which stays true exactly there and nowhere else.
 *
 * ⚠️ NATIVE IS NOT PROVEN BY THESE TESTS. jsdom has no geolocation, so every
 * test here drives an installed stub. Inside the Capacitor WKWebView the API
 * exists but may prompt oddly or never answer; the 10 s timeout is what makes
 * that a `declined`, not a hang. Founder device-verify on the next build, and
 * the iOS purpose string still describes RUN mapping only — updating it is a
 * flagged next-build item, not a change this lane may make (`ios/` untouched).
 *
 * ⛔ The cost of a decline is ONLY the typing, not the data: `handleContinue`
 * below keeps the coordinates the geocoder returns, so a typed city produces
 * the same `_latitude`/`_longitude` a device fix would have (B48 part 2).
 *
 * ## B58 — one city name, many countries
 *
 * The founder's second complaint: a bare city name is ambiguous across
 * countries, the lookup resolved it without saying which one it picked, and the
 * Country box never filled in. Two answers here, and one gap that is not this
 * lane's to close:
 *
 *   · when the resolver hands back more than one plausible place, the step
 *     STOPS PICKING and shows up to three tappable options with their region
 *     and country. Choosing one fills the City and Country fields from it;
 *   · when exactly one comes back for a query that carried NO country, the
 *     ratified match card is unchanged, with one added line saying the name is
 *     shared and a country would narrow it. Country stays optional.
 *   · ⚠️ the server can only ever answer with ONE match today, and its label
 *     carries no country at all — see the gap written up on `toCandidate`. The
 *     chooser is therefore built, tested and dormant until `/api/geocode`
 *     answers with a list. `api/` is read-only to this lane; flagged, not done.
 *
 * ## F5(b) — the athlete no longer types blind
 *
 * The other half of F5 was that typing produced no feedback whatsoever, so
 * the athlete could not tell whether the app understood a non-US place name.
 *
 * As-you-type autocomplete is NOT the fix available to us. That began as an
 * OSM policy prohibition (cross-PR safety gate #4); post-§8.3 the geocoder is
 * ours, but the rule survives on its own merit — a keystroke-rate lookup is a
 * keystroke-rate disclosure of what the athlete is typing, and the helper line
 * below promises "Nothing is searched as you type". Instead the athlete gets
 * an explicit, user-triggered "Check this location" — exactly one
 * forward-geocode call per press, through the wrapper, with the resolved place
 * echoed back. No request is ever issued on keystroke, and an in-flight check
 * is aborted when the query changes so a stale response can never overwrite a
 * newer one.
 *
 * Checking is optional. Continue works without it, and the step can always be
 * skipped outright.
 *
 * ## CD 2026-07-27 · Item 3 — ratified as shipped, with three amendments
 *
 *   1. `failed` is no longer dressed as `no-match`. A transport failure is
 *      neutral (cream/ink), not amber, and carries an inline Try again that
 *      re-runs the SAME single-tap lookup — still one request per explicit
 *      tap, so gate #4 is untouched.
 *   2. `aria-busy` on the check control while pending, and focus moves to the
 *      result slot when a check resolves (closes the F-7b marker).
 *   3. OSM attribution sits with the control it describes, as a second helper
 *      line under the check helper — not orphaned at the bottom of the step.
 *
 * The D5 gate ruling was "A, conditional": the typed-city Continue gate stays
 * ONLY because `answers.location` now persists to the canonical `_city` key in
 * this same lane (`usePersistOnboarding.ts` buildOnboardingPayload). If that
 * persistence is ever removed, the gate must drop to B (ungate) with it.
 */
/** The kinds that mean "a check finished" — the moment focus moves.
 *  ⚖️ B58 — `choose` is a finished check like any other: the answer is on
 *  screen, it just happens to be a question back. Leaving it out would land the
 *  athlete's focus nowhere near the options she now has to pick from. */
function isResolved(kind: LookupState['kind']): boolean {
  return kind === 'match' || kind === 'choose' || kind === 'no-match' || kind === 'failed';
}

export function Step2Location({
  answers,
  setAnswer,
  onContinue,
  resolveCandidates = resolveCandidatesViaGeocoder,
}: Step2Props) {
  const [lookup, setLookup] = useState<LookupState>({ kind: 'idle' });
  const [position, setPosition] = useState<PositionState>({ kind: 'idle' });
  /** Read ONCE, at mount — see `hasPositionSupport`. A value that flipped
   *  mid-step would swap the sub copy under the athlete's eyes. */
  const [canUsePosition] = useState(hasPositionSupport);
  /** Mirrors `position.kind === 'locating'` synchronously, for the same reason
   *  `isCheckingRef` exists: a double-tap fires both handlers before React
   *  re-renders, and one tap is one position request. */
  const locatingRef = useRef(false);
  /** ⛔ `getCurrentPosition` callbacks are not abortable. This is how a late
   *  answer is stopped from writing into an unmounted step. */
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);
  const slotRef = useRef<HTMLDivElement>(null);
  /** ⚖️ O2 — the destination for "None of these. Keep typing instead.", which
   *  is the one control on this step whose own copy names where focus should
   *  land next. See `handleDismissChooser`. */
  const cityRef = useRef<HTMLInputElement>(null);
  const prevKindRef = useRef<LookupState['kind']>('idle');
  /**
   * CD 2026-08-02 §3 — THE RETRY LIFECYCLE, one anatomy.
   *
   * `true` while a check that was STARTED FROM `Try again` is in flight. It does
   * two jobs, and both come straight from the ratified anatomy:
   *   · the failed note stays MOUNTED through the retry (§3.1) — `data-state`
   *     flips, the node does not, so the control the athlete just pressed is
   *     still there to hold focus;
   *   · focus does NOT move on a retry's resolve (§3.3) — amendment 2's
   *     focus-to-slot applies to a FIRST check only.
   *
   * The ref mirrors the state because the focus effect must read the value AT
   * THE RESOLVE EDGE; a state read there is one render behind.
   */
  const [retryInFlight, setRetryInFlight] = useState(false);
  const retryRef = useRef(false);
  /** Mirrors `lookup.kind === 'checking'` SYNCHRONOUSLY. `handleCheck`'s guard
   *  cannot read state — a double-tap fires both handlers before React
   *  re-renders, so a state read would let the second one through. */
  const isCheckingRef = useRef(false);

  const city = answers.manualCity?.trim() ?? '';
  const country = answers.manualCountry?.trim() ?? '';
  const query = composeQuery(city, country);
  // N-2: city required — a country-only check would spend a real Nominatim
  // request on a query Continue can never save (the two gates must agree).
  const queryTooLong = query.length > MAX_QUERY_CHARS;
  const canCheck =
    city.trim().length > 0 && query.length >= MIN_QUERY_CHARS && !queryTooLong;

  // Abort any in-flight check when the step unmounts.
  // ⚖️ B58 — and mark the step gone, so a `getCurrentPosition` callback that
  // lands after unmount drops instead of setting state. ⛔ This effect requests
  // nothing; it is cleanup only. The ONE position request in this file lives in
  // `handleFindLocation`, and a drift guard pins that.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  /** CD Item 3 amendment 2 — when a check RESOLVES, move focus to the result
   *  slot so the answer is where the athlete (and a screen reader) already is.
   *  Only on the checking → resolved edge: never on mount, never when an edit
   *  clears the slot back to idle, so we never steal focus from the input the
   *  athlete is still typing in. Delta-verify CD-N1: refocusing WITHOUT editing
   *  is not an edit — if the athlete put the cursor back in a field while the
   *  check was in flight, the resolve must not yank it (and collapse the iOS
   *  keyboard mid-correction). The move is skipped whenever focus sits in any
   *  input. */
  useEffect(() => {
    const prev = prevKindRef.current;
    prevKindRef.current = lookup.kind;
    isCheckingRef.current = lookup.kind === 'checking';

    // One choke point for clearing the retry flag: whatever ends the in-flight
    // window — resolve, abort, or an edit back to idle — passes through here.
    if (lookup.kind !== 'checking' && retryInFlight) setRetryInFlight(false);

    // ⚖️ B58 — PICKING AN OPTION UNMOUNTS THE OPTION. The chooser is replaced by
    // the match card, so the button the athlete just pressed ceases to exist and
    // the browser drops focus to `<body>` — the exact S5 defect the retry
    // anatomy was written to close, arriving through a new door. Rescue focus to
    // the slot, which is where the answer now is.
    if (prev === 'choose' && lookup.kind === 'match') {
      slotRef.current?.focus();
      return;
    }

    if (prev !== 'checking' || !isResolved(lookup.kind)) return;

    // ⚖️ CD 2026-08-02 §3.3 — FOCUS DOES NOT MOVE ON A RETRY. It stays on the
    // pressed `Try again` for the whole in-flight window AND after the result.
    // Amendment 2 is a FIRST-CHECK behaviour: on a first check the pressed
    // control is `Check this location`, which sits above a slot that was empty,
    // so moving focus takes the athlete TO the answer. On a retry the answer
    // lands in a note she is already inside — moving focus would take her out
    // of the control she just used and away from the retry she may want again.
    const wasRetry = retryRef.current;
    retryRef.current = false;
    if (wasRetry) {
      // ⚠️ AMENDED FROM THE RATIFIED TEXT, and flagged to CD (see the lane note).
      // §3.3 says focus stays on the pressed control "for the whole in-flight
      // window and after the result". That is right when the retry FAILS AGAIN:
      // the note stays mounted, `Try again` is still there, and focus never
      // moved. But when the retry SUCCEEDS, the failed note is replaced by the
      // match card — THE PRESSED CONTROL CEASES TO EXIST — and "stay on it"
      // resolves to staying on a detached node, which the browser answers by
      // dropping focus to `<body>`. Read literally, §3.3 reproduces the exact
      // defect the anatomy was written to prevent.
      // So: if the control survived, honour §3.3 and do nothing. If it did not,
      // rescue focus to the slot. ⛔ Never leave focus on `<body>` mid-form.
      const holder = document.activeElement;
      if (holder && holder !== document.body && holder.isConnected) return;
      slotRef.current?.focus();
      return;
    }

    // CD-N1 stays: refocusing without editing is not an edit. If the athlete put
    // the cursor back in a field while the check was in flight, the resolve must
    // not yank it (and collapse the iOS keyboard mid-correction).
    if (document.activeElement instanceof HTMLInputElement) return;
    slotRef.current?.focus();
  }, [lookup.kind, retryInFlight]);

  /** Any edit invalidates the previous result — drop it and cancel the
   *  request that produced it, so a late response can't land on new text. */
  const invalidateLookup = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setLookup((prev) => (prev.kind === 'idle' ? prev : { kind: 'idle' }));
  }, []);

  /**
   * ⚖️ B58 — THE COUNTRY BOX FILLS ITSELF WHEN, AND ONLY WHEN, THE ANSWER
   * CARRIES A COUNTRY.
   *
   * The founder's complaint was that it never fills. This is the write that
   * fills it, from a resolved candidate, on both the single-match path and the
   * chooser path. It is additive only: an empty box gets the answer, a box the
   * athlete has already typed in is NEVER overwritten — the field is theirs and
   * a lookup does not get to correct them.
   *
   * ⚠️ Today this is usually a no-op, and that is a server gap, not a bug here:
   * `/api/geocode` labels are "City, Region" with the country deliberately
   * dropped upstream. See the gap written up on `toCandidate`. When the server
   * starts sending a country, this fills with no further work.
   */
  const fillCountryFrom = useCallback(
    (candidate: LocationCandidate) => {
      if (!candidate.country) return;
      if (country) return;
      setAnswer({ manualCountry: candidate.country });
    },
    [country, setAnswer],
  );

  /**
   * ⚖️ B58 — THE ONE PLACE THIS APP ASKS THE PHONE WHERE IT IS, at onboarding.
   *
   * ⛔ CALLED FROM THE BUTTON'S onClick AND NOWHERE ELSE. Not from an effect,
   * not on mount, not from the check, not from Continue. That is the whole
   * consent model: the tap is the athlete asking, and the OS prompt is what
   * grants it. A drift guard pins both the single call site and the behaviour.
   *
   * ⚖️ §8.2 — `pos.coords` is the only place a precise fix exists in this
   * component, and it ends on the two rounding lines below. State, the note,
   * the reverse-geocode request and the saved answer all see city level only.
   *
   * Every unhappy ending is a calm ending: one short line, the typed field
   * still there, nothing that reads as the athlete's fault. ⚖️ O2 — there are
   * now TWO of them rather than one, split on the single `PERMISSION_DENIED`
   * bit per the CD handoff; see the `PositionState` docblock for why that is
   * not the taxonomy B58 refused.
   *
   * ⛔ STILL THE ONE CALL SITE. `Retry` on the failed note calls THIS handler
   * again; it does not open a second one. The drift guard counts invocations,
   * and one tap is still one request (`locatingRef`).
   */
  const handleFindLocation = useCallback(() => {
    if (!canUsePosition) return;
    if (locatingRef.current) return;
    locatingRef.current = true;
    setPosition({ kind: 'locating' });

    const settle = (next: PositionState) => {
      locatingRef.current = false;
      if (mountedRef.current) setPosition(next);
    };

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = roundToCityLevel(pos.coords.latitude);
        const lon = roundToCityLevel(pos.coords.longitude);
        void (async () => {
          try {
            const place = await reverseGeocode(lat, lon);
            // `reverseGeocode` answers a coords-only `displayName` when it has
            // no name for the pair, so an empty `city` is the honest test for
            // "we have a fix but not a place".
            if (place.city) {
              settle({ kind: 'located', label: place.displayName, lat, lon });
              // The resolved place lands in the City field, which stays fully
              // editable — this is a filled-in answer, not a locked one.
              if (mountedRef.current) setAnswer({ manualCity: place.displayName });
            } else {
              settle({ kind: 'unresolved', lat, lon });
            }
          } catch {
            // The fix is still good even when naming it failed. Keep it.
            // ⛔ NOT `failed`: we HAVE her location, we just have no name for
            // it. Offering a Retry here would retry the wrong thing.
            settle({ kind: 'unresolved', lat, lon });
          }
        })();
      },
      // ⚖️ O2 — one bit, two endings. Anything that is not an explicit refusal
      // is treated as a lookup that did not land, which is the retryable
      // reading and the fail-safe one: offering a Retry after a timeout costs a
      // tap, while withholding it after a timeout costs the feature.
      (err) =>
        settle(
          err?.code === PERMISSION_DENIED ? { kind: 'declined' } : { kind: 'failed' },
        ),
      // The timeout is what turns a shell that never answers into a decline
      // rather than a spinner with no end — the failure mode B48 was reported
      // against, arriving from the other direction.
      { timeout: 10_000, maximumAge: 60_000 },
    );
  }, [canUsePosition, setAnswer]);

  /** ⚖️ B58 — picking an option is the athlete SETTLING the ambiguity, so it
   *  settles the fields too: the city she chose and, when the answer carries
   *  one, its country. The chooser collapses to the ratified match card, which
   *  is the same card a single unambiguous answer has always produced. */
  const handleChoose = useCallback(
    (candidate: LocationCandidate) => {
      const patch: Partial<OnboardingAnswers> = { manualCity: candidate.city };
      // An explicit pick DOES overwrite the country box — unlike the passive
      // single-match fill, this is the athlete naming the country herself.
      if (candidate.country) patch.manualCountry = candidate.country;
      setAnswer(patch);
      setLookup({ kind: 'match', ...matchFromCandidate(candidate) });
    },
    [setAnswer],
  );

  /**
   * ⚖️ O2 / CD handoff — "None of these. Keep typing instead."
   *
   * The ratified escape from the chooser: none of the three is hers, so the
   * question closes and the typed field is hers again. ⛔ It clears the LOOKUP
   * only — the text she typed is untouched, because the invitation is to keep
   * typing, not to start over.
   *
   * ⚖️ S5, the same door the chooser already had to close on the other side:
   * the link the athlete presses is unmounted by her own press, and focus would
   * fall to `<body>`. It goes to the City field, which is literally what the
   * sentence just told her to do.
   */
  const handleDismissChooser = useCallback(() => {
    setLookup({ kind: 'idle' });
    cityRef.current?.focus();
  }, []);

  /**
   * @param fromRetry `true` only when pressed on `Try again`.
   *
   * ⚖️ CD 2026-08-02 §3.2 — THE IN-FLIGHT GUARD IS THIS EARLY RETURN, not a
   * `disabled` attribute. A disabled element is not focusable, so disabling the
   * control the athlete just pressed is itself a way to throw focus at `<body>`.
   * One-tap-one-request is enforced here and by the controller abort below;
   * `aria-disabled` is what ANNOUNCES it. ⛔ Do not "harden" this by adding
   * `disabled` back — that is the defect, not the fix.
   */
  const handleCheck = useCallback(async (fromRetry = false) => {
    if (!canCheck) return;
    if (isCheckingRef.current) return;
    // ⚠️ ARMED HERE, SYNCHRONOUSLY — not left to the effect below. The effect
    // runs after commit, so two taps dispatched in the SAME FRAME would both
    // read `false` and both pass. (The test suite does not expose that window:
    // React flushes between `fireEvent.click` calls, and the geocode wrapper's
    // 1-req/sec throttle would absorb the second request anyway — which is
    // exactly why the guard must not depend on either of them.)
    isCheckingRef.current = true;
    retryRef.current = fromRetry;
    if (fromRetry) setRetryInFlight(true);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLookup({ kind: 'checking' });
    try {
      const candidates = await resolveCandidates(query, controller.signal);
      if (controller.signal.aborted) return;
      // ⚖️ B58 — THREE OUTCOMES, and only the middle one is today's behaviour.
      // Nothing found is still `no-match`; exactly one is still the ratified
      // match card, verbatim; more than one no longer gets silently narrowed to
      // the geocoder's first guess, which is the founder's complaint in one
      // line. §8.2 rounding already happened in `toCandidate`, at capture.
      if (candidates.length === 0) {
        setLookup({ kind: 'no-match' });
      } else if (candidates.length === 1) {
        setLookup({ kind: 'match', ...matchFromCandidate(candidates[0]) });
        fillCountryFrom(candidates[0]);
      } else {
        setLookup({ kind: 'choose', candidates: candidates.slice(0, MAX_CANDIDATES) });
      }
    } catch {
      // Discriminate on OUR controller, never on the error's name. The
      // wrapper's own 10s timeout also rejects with an AbortError, so
      // name-matching swallowed a real timeout and wedged the button in
      // "Checking…" forever (review finding F-1). If we did not abort this
      // request, the athlete is owed an answer.
      if (controller.signal.aborted) return;
      setLookup({ kind: 'failed' });
    }
  }, [canCheck, query, resolveCandidates, fillCountryFrom]);

  /** Enter/Go is the likeliest first gesture on a mobile keyboard, so it
   *  triggers the check — an explicit user action, policy-compliant. It must
   *  NOT advance the step: the athlete asked to verify, not to move on. */
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      if (canCheck && lookup.kind !== 'checking') void handleCheck();
    },
    [canCheck, lookup.kind, handleCheck],
  );

  /**
   * ⚖️ B48 (2026-08-29) — CONTINUE IS THE SAVE, so Continue is where the
   * coordinates get settled.
   *
   * The defect: `geocodeCityOnSave` answers `{label, lat, lon}`, this handler
   * kept the label and threw the numbers away, and `buildOnboardingPayload`
   * had a comment explaining that onboarding "never obtains coordinates". So
   * every athlete who completed the wizard got `_city` with null
   * `_latitude`/`_longitude`, and weather + the vitamin-D band ran degraded
   * from day one on an account nobody would think to go fix in Settings.
   *
   * Two paths, one rule — never block the step for a network call:
   *
   *   1. A verified match for THIS query is already in hand → its coordinates
   *      ride out with the label, synchronously. Nothing to wait for.
   *   2. No match (the common walk — type a city, tap Continue, never tap
   *      Check) → the typed text is saved and the step advances IMMEDIATELY,
   *      and the same one-request-per-explicit-action lookup runs behind it.
   *      This is `geocodeCityOnSave` used for exactly what it is named for and
   *      what Settings' `LocationModal.handleSave` already does with it; when
   *      the athlete DID tap Check, the wrapper's cache answers it with no
   *      request at all. Gate #4 is untouched: still nothing on keystroke.
   *
   * ⛔ The background lookup is deliberately NOT wired to `abortRef` — that
   * controller is aborted on unmount, and this request has to outlive the step
   * by design. It resolves into the wizard reducer, which is mounted for the
   * rest of the flow; the server write is at Step 7, many beats later.
   *
   * ⚖️ §8.2 — every coordinate is rounded at capture, on the two lines that
   * first touch it. A precise fix is never held, never rendered, never stored.
   */
  const handleContinue = useCallback(() => {
    if (!city || query.length > MAX_QUERY_CHARS) return;
    // Prefer the geocoder's own label when the athlete verified this exact
    // query — it carries the correct region/country for international places.
    if (lookup.kind === 'match') {
      setAnswer({
        location: lookup.label,
        locationConfirmed: true,
        // Already city-level — rounded where the match was captured, above.
        locationCoords: { lat: lookup.lat, lon: lookup.lon, for: lookup.label },
      });
      onContinue();
      return;
    }

    /**
     * ⚖️ B58 — A DEVICE FIX THE ATHLETE ASKED FOR, still describing the text in
     * the field, is the best coordinate pair in the room: it is hers, it is
     * exact to the kilometre, and it cost no request.
     *
     * The `startsWith` test is the whole guard. We wrote `place.displayName`
     * into the City field; if the composed query still opens with it, the field
     * is the place the fix named (a country appended is fine and common). If
     * she typed over it, the fix describes somewhere else and is not used —
     * fail-safe direction is "no coordinates", never "someone else's".
     */
    if (position.kind === 'located' && query.startsWith(position.label)) {
      setAnswer({
        location: query,
        locationConfirmed: true,
        locationCoords: { lat: position.lat, lon: position.lon, for: query },
      });
      onContinue();
      return;
    }

    // ⚠️ `locationCoords: undefined` is load-bearing, not tidiness: Back +
    // edit + Continue must never carry the PREVIOUS city's coordinates
    // forward. Cleared here, re-established only by the resolve below.
    setAnswer({ location: query, locationConfirmed: true, locationCoords: undefined });
    onContinue();

    const savedFor = query;
    /** ⚖️ B58 — the LAST resort, used only when the typed city cannot be
     *  geocoded at all. `unresolved` means the athlete took a fix, we had no
     *  name for it, and she typed the name herself — so the pair is hers and the
     *  text is hers. The typed city still wins when it resolves, because the
     *  name is the authority on the place; this only stops a nameable fix from
     *  being thrown away when the geocoder has nothing.
     *  ⛔ Never applied after `located`: there the athlete typed OVER a name we
     *  supplied, which is a statement that she meant somewhere else. */
    const fallback =
      position.kind === 'unresolved'
        ? { lat: position.lat, lon: position.lon, for: savedFor }
        : null;
    void geocodeCityOnSave(savedFor)
      .then((match) => {
        if (!match) {
          if (fallback) setAnswer({ locationCoords: fallback });
          return;
        }
        setAnswer({
          locationCoords: {
            lat: roundToCityLevel(match.lat),
            lon: roundToCityLevel(match.lon),
            // The `for` tag is what makes a late landing safe: if the athlete
            // has since saved a different city, the consumer sees the mismatch
            // and drops the pair rather than pinning it to the wrong place.
            for: savedFor,
          },
        });
      })
      .catch(() => {
        // Best-effort by construction. The city string is already saved and
        // the athlete is already on the next step; a failed lookup costs the
        // coordinates, never the answer, and never an error surface.
        if (fallback) setAnswer({ locationCoords: fallback });
      });
  }, [city, lookup, position, query, setAnswer, onContinue]);

  /** Read outside the JSX so the narrowed `failed` branch can still ask it —
   *  inside that branch TS knows kind === 'failed' and would reject the
   *  comparison as non-overlapping. */
  const isChecking = lookup.kind === 'checking';
  const isLocating = position.kind === 'locating';
  /** ⚖️ B58 — the founder's this-city-name-exists-everywhere case, exactly: ONE
   *  answer came back for a query that named no country, so the step says the
   *  name is shared rather than presenting the geocoder's first guess as the
   *  only reading. Silent when a country was typed (the query already narrowed
   *  it) and when the answer itself carries a country. */
  const showAmbiguousHint =
    lookup.kind === 'match' && !country && !lookup.label.split(',')[2];

  /**
   * ⚖️ S14 (CD 2026-08-04, ruling 21) — A LIVE REGION ANNOUNCES EXACTLY THE
   * SENTENCE THE EYE CAN FIND. These constants are the ONE source both the
   * visible note and the announcement render from, so the two copies cannot
   * drift — hearing one answer and reading another is the defect S14 exists
   * to kill. The visible node may SPLIT a sentence typographically (the match
   * card renders kicker + label as two lines); it never rewords it.
   */
  const NO_MATCH_TEXT =
    'We couldn’t find that place. Continue anyway. We’ll save what you typed.';
  const FAILED_TEXT =
    'Couldn’t check right now. You can continue. We’ll save what you typed.';
  const MATCH_KICKER = 'We found';
  /**
   * ⚖️ O2 — CD HAS NOW RULED ON B58's PROVISIONAL COPY, and these are the
   * ratified strings (CD onboarding-chapters handoff, founder-ratified
   * 2026-08-30, "New screen 3 — Find my location"). B58 shipped its wording
   * explicitly marked "📋 all flagged to CD ... so CD owns the final wording";
   * this is CD owning it. Replaced verbatim, and the drift guards that pinned
   * the provisional sentences were updated to the ratified ones — see the O2
   * lane report for the line-by-line table.
   *
   * House rules still observed: no em or en dashes in the new strings, no
   * exclamation marks, the word "safe" appears nowhere.
   *
   * ⛔ `CHOOSE_TEXT` is announced verbatim (S14): the live region says exactly
   * the sentence that leads the chooser, never a summary of the options. In the
   * ratified design that sentence is also the card's KICKER, which is a
   * typographic treatment of the same words, not a reword.
   */
  const CHOOSE_TEXT = 'More than one match. Which one?';
  const CHOOSE_DISMISS = 'None of these. Keep typing instead.';
  /** ⚠️ NOT ruled on by CD (the handoff has no shared-name line), so B58's
   *  provisional sentence stands verbatim. 📋 Still flagged. */
  const AMBIGUOUS_HINT =
    'Places in different countries share this name. Add a country and check again if this is not yours.';
  /**
   * ⚖️ The position result is now a CARD, not a sentence: signal-soft, kicker
   * plus the city in serif — the same anatomy the check's own answer has always
   * used, which is the point. Two answers to "where do you train?" that look
   * like two different kinds of thing is the drift this replaces.
   *
   * ⚠️ B58's "Location set to {label}. Edit the city below if that is not
   * right." IS RETIRED with it. The card carries no edit instruction because
   * the ratified design does not have one; the field directly beneath is
   * filled, focusable and unlocked, which is the affordance the sentence was
   * describing. 📋 Called out in the O2 lane report as the one place the
   * ratified design drops a sentence rather than replacing it.
   */
  const POSITION_KICKER = 'Your location';
  const LOCATING_TEXT = 'Finding your location…';
  const DECLINED_TEXT = 'No problem. Type your city instead.';
  const POSITION_FAILED_TEXT = 'We could not get your location.';

  /**
   * ⚖️ CD 2026-08-02 §3.4 — what the permanent `role="status"` span announces.
   *
   * ⛔ RESULTS ONLY, never the pending state. C14's DONE-WHEN is EXACTLY ONE
   * ANNOUNCEMENT PER CHECK; announcing `Looking up…` and then the answer is two.
   * The spinner row is `aria-hidden` for the same reason.
   *
   * ⛔ It carries no control's label. The failed announcement deliberately stops
   * before `Try again` — the button's name is reachable by navigation, and a
   * live region that reads out a control is announcing the wrong noun.
   */
  const lookupAnnouncement =
    lookup.kind === 'match'
      ? `${MATCH_KICKER} ${lookup.label}`
      : lookup.kind === 'choose'
        ? CHOOSE_TEXT
        : lookup.kind === 'no-match'
          ? NO_MATCH_TEXT
          : lookup.kind === 'failed'
            ? FAILED_TEXT
            : '';

  /**
   * ⚖️ B58 — the position result, in the SAME words the eye reads (S14) and
   * through the SAME single live region (CD §3.4 / CD-N3 — there is exactly one
   * on this step, and a second one is pinned against).
   *
   * ⛔ `locating` has no line. Announcing a pending state and then its answer is
   * two announcements for one action, which is the thing C14's DONE-WHEN
   * forbids; the button's own label and `aria-busy` carry the wait.
   */
  const positionNote =
    position.kind === 'located'
      ? // S14 allows the typographic split the card makes (kicker on one line,
        // city on the next); the announcement is the same words in the same
        // order, exactly as the check's own match card has always done it.
        `${POSITION_KICKER} ${position.label}`
      : position.kind === 'unresolved'
        ? 'Location set. Type your city below so your coaches can name it.'
        : position.kind === 'declined'
          ? DECLINED_TEXT
          : position.kind === 'failed'
            ? POSITION_FAILED_TEXT
            : '';

  // A lookup result is the more specific answer and takes the region when both
  // have something to say; they cannot both be fresh, because a check and a
  // position request are two different taps. B442 — the length sentence comes
  // between them: it blocks Continue, so it outranks a position note, and it
  // rides this SAME single live region (a second one is pinned against).
  const announcement =
    lookupAnnouncement || (queryTooLong ? LIMIT_TEXT : '') || positionNote;

  /**
   * ⚖️ O2 / CD handoff — "States (all render in the existing reserved lookup
   * slot so nothing shifts under the finger)". The position result moved OUT of
   * the row beside the button and INTO the slot the check results already own,
   * so this step has ONE place where answers appear instead of two.
   *
   * The slot can only show one thing, so this is the visual half of the
   * precedence `announcement` already applies: a live lookup result owns the
   * slot, and the position result has it the rest of the time. They cannot both
   * be fresh — a check and a position request are two different taps, and
   * editing the City field retires whichever one described it.
   */
  const positionOwnsSlot = lookup.kind === 'idle' && position.kind !== 'idle';

  const handleSkip = useCallback(() => {
    abortRef.current?.abort();
    // `location` + `locationConfirmed` are the canon and are cleared — and
    // B48's `locationCoords` with them, or a skip after a check would leave
    // coordinates behind for a city the athlete declined to save. The raw
    // `manualCity` / `manualCountry` keystrokes are deliberately retained so
    // Back-navigation re-hydrates what the athlete typed.
    setAnswer({ location: undefined, locationConfirmed: false, locationCoords: undefined });
    onContinue();
  }, [setAnswer, onContinue]);

  return (
    <>
      <StepHeading>Where do you train?</StepHeading>
      {/* ⚖️ B58 — THE FIRST SENTENCE IS BRANCHED, and the branch is the point.
          B48 added one sentence saying Tayro does not read the phone's location
          here. That sentence was true then and is FALSE the moment the button
          renders, so it is RETIRED on that branch rather than left to become
          the kind of stale promise §8.3 had to go clean up elsewhere. It
          survives verbatim in the one place it is still true: a shell with no
          position API, where there is no button and typing really is the only
          path. The two ratified sentences after it are verbatim in both
          branches. 📋 Both first sentences are PROVISIONAL and flagged to CD —
          ratified surface, CD 2026-07-27 Item 3. */}
      <StepSub>
        {canUsePosition
          ? 'Tap Find my location, or type your city.'
          : 'Tayro doesn’t read your phone’s location here. Type your city instead.'}{' '}
        Your coaches use this to factor weather and elevation into your plan.
        You can add or change it later in Settings.
      </StepSub>

      {/* ⚖️ B58 — THE BUTTON, and the founder's ruling in one control: the tap
          is the consent, the OS prompt seals it, and the typed fields below are
          the fallback for anyone who declines. It sits ABOVE the fields because
          it is the offered path; the answer it produces lands in the City field
          directly beneath, which stays fully editable.
          ⛔ Rendered only where a position API exists. Elsewhere there is no
          control at all, no spinner, and no claim — F5(a)'s guarantee intact. */}
      {canUsePosition && (
        <div className={styles.checkRow}>
          <button
            type="button"
            className={styles.findBtn}
            onClick={handleFindLocation}
            // ⚖️ S5, same rule as the check control — ⛔ NEVER the `disabled`
            // ATTRIBUTE. A disabled element is not focusable, so disabling the
            // control the athlete just pressed drops focus to `<body>` for the
            // whole in-flight window. `locatingRef`'s early return is what
            // enforces one tap, one request.
            aria-disabled={isLocating || undefined}
            aria-busy={isLocating}
          >
            {/* ⚖️ O2 / CD handoff — "crosshair glyph in marine". `sys.locate`
                IS that glyph and it is already in the ratified registry
                (`icons.json#icons`), so nothing needed flagging for a new pick
                and no inline art was authored here.
                ⛔ UNGATED, deliberately, and the ONE place in this file that
                departs from `useIconSystem`'s swap pattern. That pattern exists
                so a MIGRATED surface can roll back to the emoji it used to
                paint; this control is NEW and its flag-off arm would be an
                empty box, i.e. a rollback target that never shipped. The glyph
                is `currentColor` by registry rule — the marine comes from
                `.findGlyph`'s own colour, never from the icon.
                ⛔ No `label`: the button's own text says "Find my location",
                and labelling the glyph would make a screen reader say it
                twice. */}
            <span className={styles.findGlyph} aria-hidden="true">
              <Icon name="sys.locate" size={16} />
            </span>
            {isLocating ? 'Locating…' : 'Find my location'}
          </button>
          {/* ⚖️ O2 / CD handoff — the ratified helper. It states the two facts
              that make the tap safe to make: it costs exactly one lookup, and
              the athlete's own device asks before anything happens. ⛔ The
              RESULT no longer lives here: every position state renders in the
              reserved slot below (see `positionOwnsSlot`). */}
          <span className={styles.checkHelp}>
            One tap, one lookup. Your device asks for permission first.
          </span>
        </div>
      )}

      <div className={styles.fields}>
        <label className={styles.field}>
          <span className={styles.label}>City</span>
          <input
            ref={cityRef}
            className={styles.input}
            type="text"
            value={answers.manualCity ?? ''}
            onChange={(e) => {
              invalidateLookup();
              // ⚖️ B58 — the position note describes THIS field, so an edit
              // retires it. `unresolved` is the exception and the reason the
              // guard is written this way round: there the athlete was ASKED to
              // type the city for a fix we could not name, so typing is her
              // completing the answer, not replacing it, and the coordinates
              // must survive the keystroke that finishes the job.
              setPosition((p) => (p.kind === 'unresolved' ? p : { kind: 'idle' }));
              setAnswer({ manualCity: e.target.value });
            }}
            onKeyDown={handleInputKeyDown}
            placeholder="e.g. Bucaramanga · Boulder · Tokyo"
            enterKeyHint="search"
            autoComplete="off"
            aria-invalid={queryTooLong || undefined}
            autoFocus
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Country (optional)</span>
          <input
            className={styles.input}
            type="text"
            value={answers.manualCountry ?? ''}
            onChange={(e) => {
              invalidateLookup();
              setAnswer({ manualCountry: e.target.value });
            }}
            onKeyDown={handleInputKeyDown}
            placeholder="e.g. Colombia · United States · Japan"
            enterKeyHint="search"
            autoComplete="off"
            aria-invalid={queryTooLong || undefined}
          />
        </label>
      </div>

      <div className={styles.checkRow}>
        <button
          type="button"
          className={styles.checkBtn}
          onClick={() => void handleCheck(false)}
          // ⚖️ CD 2026-08-02 §3 / S5 — ⛔ NO `disabled` ATTRIBUTE. This control
          // previously carried `disabled={!canCheck || kind === 'checking'}`,
          // which meant a FIRST check disabled the very control the athlete had
          // just pressed: a disabled element is not focusable, so focus fell to
          // `<body>` for the whole in-flight window. The resolve effect then
          // rescued focus to the slot, which is exactly why the defect read as
          // retry-only in the register — it was never retry-only.
          aria-disabled={!canCheck || isChecking || undefined}
          aria-busy={isChecking}
        >
          {lookup.kind === 'checking' ? 'Checking…' : 'Check this location'}
        </button>
        <span className={styles.checkHelp}>
          Optional: one lookup when you tap it. Nothing is searched as you type.
        </span>
        {/* ⚖️ §8.3 (2026-08-16) — CD Item 3 amendment 3 put an OSM credit here,
            on the correct principle that attribution belongs with the control
            it describes. The control now describes a different geocoder: the
            lookup goes to our own /api/geocode (Apple behind it), so the OSM
            credit is not stale copy, it is a false statement about where the
            athlete's typed city went. Removed, not reworded — a geocode that
            draws no map needs no visible Apple mark either, and REL-8's marks
            cover the surfaces that render Apple DATA. */}
      </div>

      {/* ⚖️ CD 2026-08-02 §3.4 / CD-N3 — THE FOCUS ANCHOR IS NOT THE LIVE REGION.
          They were one node: `<div role="status" aria-live="polite" tabIndex={-1}>`
          was live region, focus target and result container at once. Two
          consequences, both real: the RETRY BUTTON'S OWN LABEL sat inside the
          announced region, so the announcement carried the name of a control
          rather than an answer; and any structural change to the result markup
          silently changed what gets announced.
          The slot below is now the FOCUS ANCHOR ONLY — `tabIndex={-1}`, no
          `aria-live`, no `role`. The announcement lives in the permanently
          mounted status span above it. ⛔ Do not put `aria-live` back here. */}
      <span className={styles.srStatus} role="status">
        {announcement}
      </span>
      <div ref={slotRef} className={styles.lookupSlot} tabIndex={-1}>
        {/* ⚖️ O2 / CD handoff — THE POSITION STATES, in the reserved slot.
            Rendered only while no lookup result is claiming it; see
            `positionOwnsSlot`. */}
        {positionOwnsSlot && position.kind === 'locating' && (
          // Neutral cream-2 note + spinner, ratified. ⛔ The live region stays
          // SILENT here: C14's DONE-WHEN is one announcement per action, and
          // the button's own `Locating…` label plus `aria-busy` already carry
          // the wait. This is the same posture the check's own `Looking up…`
          // row has always had, which is why the two look alike.
          <div className={styles.lookupPending}>
            <span className={styles.spinner} aria-hidden />
            <span>{LOCATING_TEXT}</span>
          </div>
        )}
        {positionOwnsSlot && position.kind === 'located' && (
          // The ratified match card: signal-soft, kicker, serif city line. The
          // SAME anatomy the check's answer uses, because it is the same
          // answer arriving by a different door.
          <div className={styles.matchCard}>
            <div className={styles.matchKicker}>{POSITION_KICKER}</div>
            <div className={styles.matchLabel}>{position.label}</div>
          </div>
        )}
        {positionOwnsSlot && position.kind === 'unresolved' && (
          // ⛔ `neutral`, not `failed`: we HAVE her fix. Nothing went wrong
          // here, and no Retry is offered because there is nothing to retry.
          <div className={styles.lookupNote} data-state="neutral">
            <span>{positionNote}</span>
          </div>
        )}
        {positionOwnsSlot && position.kind === 'declined' && (
          // ⛔ NEUTRAL, and NO RETRY. She said no on purpose; the app does not
          // ask again and does not dress her choice as a fault. The amber is
          // reserved for statements about what she TYPED.
          <div className={styles.lookupNote} data-state="neutral">
            <span>{DECLINED_TEXT}</span>
          </div>
        )}
        {positionOwnsSlot && position.kind === 'failed' && (
          // Neutral note + the ratified underlined marine Retry. ⛔ It calls
          // `handleFindLocation`, the ONE position call site — a retry is
          // another tap, not another code path.
          <div className={styles.lookupNote} data-state="failed">
            <span>{POSITION_FAILED_TEXT}</span>
            <button
              type="button"
              className={styles.retryBtn}
              onClick={handleFindLocation}
              // ⛔ S5 again: never the `disabled` attribute on a control that
              // may be holding focus.
              aria-disabled={isLocating || undefined}
            >
              Retry
            </button>
          </div>
        )}
        {/* ⛔ The first-check pending row ONLY. A retry's pending state renders
            inside the failed note (§3.1), which stays mounted — rendering both
            would put two "checking" affordances on screen at once. */}
        {isChecking && !retryInFlight && (
          <div className={styles.lookupPending}>
            <span className={styles.spinner} aria-hidden />
            <span>Looking up…</span>
          </div>
        )}
        {lookup.kind === 'match' && (
          <div className={styles.matchCard}>
            <div className={styles.matchKicker}>{MATCH_KICKER}</div>
            <div className={styles.matchLabel}>{lookup.label}</div>
            {/* ⚖️ B58 — the card itself is RATIFIED AND UNTOUCHED. This line is
                added under it, and only for the ambiguous reading: one answer,
                no country anywhere in the question or the answer. It is a
                statement about the QUESTION, not a warning about the athlete's
                input, so it borrows no amber. 📋 Copy provisional, flagged to
                CD, as is the decision to sit it inside the card. */}
            {showAmbiguousHint && (
              <div className={styles.checkHelp}>{AMBIGUOUS_HINT}</div>
            )}
          </div>
        )}
        {/* ⚖️ B58 — THE CHOOSER. Reached only when the resolver hands back more
            than one plausible place; the step then asks instead of picking.
            Options are buttons, in a list, each carrying the parts that tell
            two same-named places apart.
            ⛔ The lead sentence is the live region's words verbatim (S14).
            ⚠️ DORMANT against today's server, which can only answer with one
            match — see the prop docblock and the gap on `toCandidate`.
            ⚖️ O2 — CD HAS NOW RULED ON THE VISUAL TREATMENT B58 flagged. B58
            borrowed `.lookupNote` + `.checkBtn` and said so, on the reasoning
            that a dormant path is the wrong place to spend new CSS. The
            handoff ratifies a signal-soft card with a kicker and OptionRow
            grammar, so the borrowed classes are replaced by the real ones and
            each option becomes the shipped `OptionRow` primitive — the house
            pattern the handoff names for exactly this shape.
            ⛔ `selected` is always false: these are three readings of one
            question, and the pick SETTLES the question rather than checking a
            box that stays checked. The card is gone the instant one is chosen.
            ⚠️ Still DORMANT against today's server, which can only answer with
            one match — see the prop docblock and the gap on `toCandidate`. */}
        {lookup.kind === 'choose' && (
          <div className={styles.chooseCard}>
            {/* The kicker IS the announced sentence (S14): a typographic
                treatment of the same words, never a reword of them. */}
            <div className={styles.matchKicker}>{CHOOSE_TEXT}</div>
            <div className={styles.chooseOptions}>
              {lookup.candidates.map((candidate) => (
                <OptionRow
                  key={`${candidate.label}:${candidate.lat},${candidate.lon}`}
                  label={candidate.city}
                  sub={[candidate.region, candidate.country].filter(Boolean).join(', ')}
                  selected={false}
                  onClick={() => handleChoose(candidate)}
                  // ⚖️ The accessible name is the WHOLE place, in one string.
                  // Split across label and sub it would be announced as two
                  // fragments, and telling two same-named cities apart is the
                  // entire job of this control.
                  aria-label={candidateLine(candidate)}
                />
              ))}
            </div>
            <button
              type="button"
              className={styles.chooseDismiss}
              onClick={handleDismissChooser}
            >
              {CHOOSE_DISMISS}
            </button>
          </div>
        )}
        {lookup.kind === 'no-match' && (
          <div className={styles.lookupNote} data-state="warn">
            {NO_MATCH_TEXT}
          </div>
        )}
        {/* B442 — a statement about what she TYPED, but it BLOCKS Continue, so it
            is an error: red text, not the amber of the no-match note (about 2.3:1
            as text, harvest UX M1). Nothing is searched for text this long, so
            it can never share the slot with a lookup result. */}
        {queryTooLong && lookup.kind === 'idle' && (
          <div className={styles.lookupNote} data-state="error">
            {LIMIT_TEXT}
          </div>
        )}
        {/* CD Item 3 amendment 1 — a transport failure is NOT a statement
            about the athlete's input, so it does not borrow the amber
            no-match treatment. Neutral, plus a retry in reach. */}
        {/* ⚖️ CD 2026-08-02 §3.1 — THE FAILED NOTE STAYS MOUNTED THROUGH ITS OWN
            RETRY. It transitions IN PLACE: failed → checking → result. The
            `data-state` flips; THE NODE DOES NOT. Everything else in the anatomy
            depends on this — if the note unmounts, the `Try again` the athlete
            just pressed goes with it, and no amount of `aria-disabled` can keep
            focus on a control that no longer exists. */}
        {(lookup.kind === 'failed' || (isChecking && retryInFlight)) && (
          <div
            className={styles.lookupNote}
            data-state={isChecking ? 'checking' : 'failed'}
          >
            <span>
              {isChecking ? 'Checking again…' : FAILED_TEXT}
            </span>
            {/* ⛔ NEVER `disabled` (S5). A disabled element is not focusable, so
                disabling the control just pressed throws focus at `<body>` — the
                precise defect this anatomy exists to close. `aria-disabled`
                ANNOUNCES the in-flight state while the button stays focusable
                and stays focused; `handleCheck`'s early return is what actually
                enforces one-tap-one-request. */}
            <button
              type="button"
              className={styles.retryBtn}
              onClick={() => void handleCheck(true)}
              aria-disabled={isChecking || undefined}
            >
              Try again
            </button>
          </div>
        )}
      </div>

      <StepCta onClick={handleContinue} disabled={!city || queryTooLong}>
        Continue
      </StepCta>
      <TertiaryLink onClick={handleSkip}>
        Skip for now. My coaches can ask later.
      </TertiaryLink>

    </>
  );
}
